import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  createOfflineOrchestrator,
  ONLINE_ONLY_MESSAGES,
  shouldBlockFirstLaunchOffline,
  canLoginOffline,
  type OfflineBranch,
  type OfflineLoginCredentials,
  type CacheAuthProfileInput,
  type OfflineAuthProfile,
  type OfflineSalePayload,
  type OfflineRefundPayload,
  type PendingOp,
  type OfflineReceiptHistoryEntry,
  type OfflineSyncOrchestrator,
} from '@shared/offline';
import type { ProductCategory, ProductWithStock } from '../domain/models';
import { databaseConfig } from '../config/database';
import { createSale, createRefund } from '../data/api/salesApiClient';
import type { CreateSalePayload } from '../data/api/salesApiClient';
import { supportsOfflinePos } from '../runtime/frontofficeRuntime';
import {
  didRecoverApiConnectivity,
  dispatchApiOnline,
} from './offlineConnectivityEvents';
import { webOfflinePosChrome } from './webOfflinePosChrome';
import { createElectronOfflineAuthCrypto } from './offlineAuthHelpers';
import {
  mapLocalCategoriesToDomain,
  mapLocalProductsToDomain,
} from './localCatalogAdapter';

export type LocalProductsPage = {
  products: ProductWithStock[];
  total: number;
  hasMore: boolean;
};

type OfflinePosContextValue = {
  /** Electron kiosk reads catalog from local store; browser uses REST only. */
  usesLocalCatalog: boolean;
  isOnline: boolean;
  connectivityReady: boolean;
  catalogReady: boolean;
  catalogRevision: number;
  isSyncing: boolean;
  lastSyncAt: string | null;
  pendingCount: number;
  firstLaunchBlocked: boolean;
  firstLaunchMessage: string;
  canLoginOffline: boolean;
  offlineLoginUnavailableMessage: string;
  onlineOnlyMessage: typeof ONLINE_ONLY_MESSAGES;
  refreshConnectivity: () => Promise<boolean>;
  syncNow: (accessToken: string, branchId: string) => Promise<void>;
  ensureCatalogOrThrow: () => void;
  getCachedBranches: () => OfflineBranch[];
  cacheBranches: (branches: OfflineBranch[]) => void;
  cacheAuthProfile: (input: CacheAuthProfileInput) => Promise<OfflineAuthProfile | null>;
  verifyOfflineLogin: (
    credentials: OfflineLoginCredentials,
  ) => Promise<{ ok: true; profile: OfflineAuthProfile } | { ok: false; reason: string }>;
  unsealProfileRefreshToken: (profile: OfflineAuthProfile) => Promise<string | null>;
  removeAuthProfile: (username: string, branchId: string) => void;
  searchLocalProducts: (query: string, branchId: string) => ProductWithStock[];
  getLocalCategories: (branchId: string) => ProductCategory[];
  getLocalProductsByCategory: (
    categoryId: string,
    branchId: string,
    page?: number,
    limit?: number,
  ) => LocalProductsPage;
  getLocalBranchProducts: (
    branchId: string,
    page?: number,
    limit?: number,
  ) => LocalProductsPage;
  submitSale: (
    accessToken: string,
    payload: CreateSalePayload,
    options?: { cashierName?: string },
  ) => Promise<{ mode: 'online' | 'offline'; receiptId: string; pending?: PendingOp }>;
  submitRefund: (
    accessToken: string,
    payload: OfflineRefundPayload,
  ) => Promise<{ mode: 'online' | 'offline'; pending?: PendingOp }>;
  getLocalReceiptHistory: (branchId: string) => OfflineReceiptHistoryEntry[];
  pullReceiptHistory: (accessToken: string, branchId: string) => Promise<number>;
  requireOnline: (feature: 'inventaire' | 'cloture' | 'profile') => string | null;
};

const OfflinePosContext = createContext<OfflinePosContextValue | null>(null);

function createHttpClient(apiBase: string) {
  const base = apiBase.replace(/\/$/, '');
  return {
    async getJson(path: string, accessToken: string) {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      const res = await fetch(`${base}${path}`, { headers });
      if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
      return res.json();
    },
    async postJson(path: string, accessToken: string, body: unknown) {
      const res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, status: res.status, data };
    },
  };
}

function toOfflineSalePayload(payload: CreateSalePayload): OfflineSalePayload {
  const items = payload.items.map(
    ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
      isFreePrice,
      categoryId,
    }) => ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
      ...(isFreePrice !== undefined ? { isFreePrice } : {}),
      ...(categoryId !== undefined ? { categoryId } : {}),
    }),
  );
  return {
    ...payload,
    items,
    payment: {
      method: String(payload.payment.method),
      amountPaid: payload.payment.amountPaid,
      cashGiven: payload.payment.cashGiven,
      cashReturned: payload.payment.cashReturned,
    },
  };
}

function WebOnlinePosProvider({ children }: { children: ReactNode }) {
  const chrome = webOfflinePosChrome({ supportsOfflinePos: false });
  // Web FO is online-only: never surface offline-POS connectivity state.
  const [connectivityReady, setConnectivityReady] = useState(false);

  const refreshConnectivity = useCallback(async () => {
    try {
      const response = await fetch(
        `${databaseConfig.mongodb.apiBaseUrl.replace(/\/$/, '')}/api/health`,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: AbortSignal.timeout(3000),
        },
      );
      return response.ok;
    } catch {
      return false;
    } finally {
      setConnectivityReady(true);
    }
  }, []);

  useEffect(() => {
    void refreshConnectivity();
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        void refreshConnectivity();
      }
    }, 10_000);
    const onConnectivityChange = () => {
      void refreshConnectivity();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshConnectivity();
      }
    };
    window.addEventListener('online', onConnectivityChange);
    window.addEventListener('offline', onConnectivityChange);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('online', onConnectivityChange);
      window.removeEventListener('offline', onConnectivityChange);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshConnectivity]);

  const syncNow = useCallback(async () => {
    // Browser FO never downloads or persists an offline catalog.
  }, []);

  const ensureCatalogOrThrow = useCallback(() => {
    // There is no local catalog in the browser runtime.
  }, []);

  const searchLocalProducts = useCallback(() => [], []);
  const getLocalCategories = useCallback(() => [], []);
  const getLocalProductsByCategory = useCallback(
    (): LocalProductsPage => ({ products: [], total: 0, hasMore: false }),
    [],
  );
  const getLocalBranchProducts = useCallback(
    (): LocalProductsPage => ({ products: [], total: 0, hasMore: false }),
    [],
  );

  const getLocalReceiptHistory = useCallback(() => [] as OfflineReceiptHistoryEntry[], []);
  const pullReceiptHistory = useCallback(async () => 0, []);

  const submitSale = useCallback(
    async (accessToken: string, payload: CreateSalePayload) => {
      const sale = await createSale(accessToken, payload);
      return { mode: 'online' as const, receiptId: sale.receiptId };
    },
    [],
  );

  const submitRefund = useCallback(
    async (accessToken: string, payload: OfflineRefundPayload) => {
      await createRefund(accessToken, payload);
      return { mode: 'online' as const };
    },
    [],
  );

  const requireOnline = useCallback(
    (_feature: 'inventaire' | 'cloture' | 'profile') => null,
    [],
  );

  const noopAuth = {
    getCachedBranches: () => [] as OfflineBranch[],
    cacheBranches: () => {},
    cacheAuthProfile: async () => null,
    verifyOfflineLogin: async () => ({
      ok: false as const,
      reason: ONLINE_ONLY_MESSAGES.offline_login_unavailable,
    }),
    unsealProfileRefreshToken: async () => null,
    removeAuthProfile: () => {},
  };

  const value = useMemo<OfflinePosContextValue>(
    () => ({
      usesLocalCatalog: false,
      isOnline: true,
      connectivityReady,
      catalogReady: true,
      catalogRevision: 0,
      isSyncing: false,
      lastSyncAt: null,
      pendingCount: 0,
      firstLaunchBlocked: false,
      firstLaunchMessage: ONLINE_ONLY_MESSAGES.first_launch,
      canLoginOffline: chrome.canLoginOffline,
      offlineLoginUnavailableMessage: ONLINE_ONLY_MESSAGES.offline_login_unavailable,
      onlineOnlyMessage: ONLINE_ONLY_MESSAGES,
      refreshConnectivity,
      syncNow,
      ensureCatalogOrThrow,
      ...noopAuth,
      searchLocalProducts,
      getLocalCategories,
      getLocalProductsByCategory,
      getLocalBranchProducts,
      submitSale,
      submitRefund,
      getLocalReceiptHistory,
      pullReceiptHistory,
      requireOnline,
    }),
    [
      chrome.canLoginOffline,
      connectivityReady,
      ensureCatalogOrThrow,
      getLocalBranchProducts,
      getLocalCategories,
      getLocalProductsByCategory,
      getLocalReceiptHistory,
      pullReceiptHistory,
      refreshConnectivity,
      requireOnline,
      searchLocalProducts,
      submitRefund,
      submitSale,
      syncNow,
    ],
  );

  return (
    <OfflinePosContext.Provider value={value}>{children}</OfflinePosContext.Provider>
  );
}

function ElectronOfflinePosProvider({ children }: { children: ReactNode }) {
  const engineRef = useRef<OfflineSyncOrchestrator | null>(null);
  if (!engineRef.current) {
    engineRef.current = createOfflineOrchestrator({
      http: createHttpClient(databaseConfig.mongodb.apiBaseUrl),
      authCrypto: createElectronOfflineAuthCrypto(),
    });
  }
  const engine = engineRef.current;

  const [isOnline, setIsOnline] = useState(false);
  const [connectivityReady, setConnectivityReady] = useState(false);
  const [catalogReady, setCatalogReady] = useState(engine.hasCatalog());
  const [catalogRevision, setCatalogRevision] = useState(engine.getCatalogRevision());
  const [pendingCount, setPendingCount] = useState(engine.pendingCount());
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(engine.getLastSyncAt());
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const sessionRef = useRef({ accessToken: '', branchId: '' });
  const wasApiOnlineRef = useRef(false);

  const refreshFromEngine = useCallback(() => {
    setCatalogReady(engine.hasCatalog());
    setPendingCount(engine.pendingCount());
      setIsOnline(engine.isOnlineCached());
    setCatalogRevision(engine.getCatalogRevision());
    setLastSyncAt(engine.getLastSyncAt());
  }, [engine]);

  const refreshConnectivity = useCallback(async () => {
    try {
      const online = await engine.checkOnline();
      setIsOnline(online);
      if (didRecoverApiConnectivity(wasApiOnlineRef.current, online)) {
        dispatchApiOnline();
      }
      wasApiOnlineRef.current = online;
      refreshFromEngine();
      return online;
    } finally {
      setConnectivityReady(true);
    }
  }, [engine, refreshFromEngine]);

  const syncNow = useCallback(
    async (accessToken: string, branchId: string) => {
      sessionRef.current = { accessToken, branchId };
      if (syncInFlightRef.current) {
        await syncInFlightRef.current;
        return;
      }
      const run = (async () => {
        setIsSyncing(true);
        try {
          await engine.syncCycle(accessToken, branchId);
          refreshFromEngine();
        } finally {
          setIsSyncing(false);
        }
      })();
      syncInFlightRef.current = run;
      try {
        await run;
      } finally {
        if (syncInFlightRef.current === run) {
          syncInFlightRef.current = null;
        }
      }
    },
    [engine, refreshFromEngine],
  );

  useEffect(() => {
    void refreshConnectivity();
    const onOfflineAuthLogout = (event: Event) => {
      const detail = (event as CustomEvent<{ username?: string; branchId?: string }>).detail;
      if (detail?.username && detail?.branchId) {
        engine.removeAuthProfile(detail.username, detail.branchId);
        refreshFromEngine();
      }
    };
    window.addEventListener('incashflow:offline-auth-logout', onOfflineAuthLogout);
    const onTerminalReset = () => {
      engine.wipeLocalData();
      refreshFromEngine();
    };
    window.addEventListener('incashflow:terminal-reset', onTerminalReset);
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      void refreshConnectivity().then((online) => {
        const { accessToken, branchId } = sessionRef.current;
        if (online && accessToken && branchId) {
          void syncNow(accessToken, branchId);
        }
      });
    }, 10_000);
    const onOnline = () => {
      void refreshConnectivity().then((online) => {
        const { accessToken, branchId } = sessionRef.current;
        if (online && accessToken && branchId) {
          void syncNow(accessToken, branchId);
        }
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshConnectivity();
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('incashflow:offline-auth-logout', onOfflineAuthLogout);
      window.clearInterval(id);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [engine, refreshConnectivity, refreshFromEngine, syncNow]);

  const firstLaunchBlocked =
    connectivityReady &&
    shouldBlockFirstLaunchOffline({
      isOnline,
      catalog: engine.getState().catalog,
    });

  const loginOfflineAllowed = canLoginOffline({
    supportsOfflinePos: true,
    isOnline,
    catalog: engine.getState().catalog,
    authProfiles: engine.getState().authProfiles,
  });

  const getCachedBranches = useCallback(() => engine.getCachedBranches(), [engine]);
  const cacheBranches = useCallback(
    (branches: OfflineBranch[]) => {
      engine.cacheBranches(branches);
      refreshFromEngine();
    },
    [engine, refreshFromEngine],
  );
  const cacheAuthProfile = useCallback(
    async (input: CacheAuthProfileInput) => {
      const profile = await engine.cacheAuthProfile(input);
      refreshFromEngine();
      return profile;
    },
    [engine, refreshFromEngine],
  );
  const verifyOfflineLogin = useCallback(
    async (credentials: OfflineLoginCredentials) => engine.verifyOfflineLogin(credentials),
    [engine],
  );
  const unsealProfileRefreshToken = useCallback(
    async (profile: OfflineAuthProfile) => engine.unsealProfileRefreshToken(profile),
    [engine],
  );
  const removeAuthProfile = useCallback(
    (username: string, branchId: string) => {
      engine.removeAuthProfile(username, branchId);
      refreshFromEngine();
    },
    [engine, refreshFromEngine],
  );

  const requireOnline = useCallback(
    (feature: 'inventaire' | 'cloture' | 'profile') => {
      if (isOnline) return null;
      return ONLINE_ONLY_MESSAGES[feature];
    },
    [isOnline],
  );

  const ensureCatalogOrThrow = useCallback(() => {
    if (shouldBlockFirstLaunchOffline({ isOnline, catalog: engine.getState().catalog })) {
      throw new Error(ONLINE_ONLY_MESSAGES.first_launch);
    }
  }, [engine, isOnline]);

  const searchLocalProducts = useCallback(
    (query: string, branchId: string) =>
      mapLocalProductsToDomain(engine.searchLocalProducts(query, branchId), branchId),
    [engine],
  );

  const getLocalCategories = useCallback(
    (_branchId: string) => mapLocalCategoriesToDomain(engine.getLocalCategoriesList()),
    [engine],
  );

  const getLocalProductsByCategory = useCallback(
    (categoryId: string, branchId: string, page = 1, limit = 50): LocalProductsPage => {
      const result = engine.getLocalProductsByCategory(categoryId, branchId, page, limit);
      return {
        products: mapLocalProductsToDomain(result.products, branchId),
        total: result.total,
        hasMore: result.hasMore,
      };
    },
    [engine],
  );

  const getLocalBranchProducts = useCallback(
    (branchId: string, page = 1, limit = 100): LocalProductsPage => {
      const result = engine.getAllLocalBranchProducts(branchId, page, limit);
      return {
        products: mapLocalProductsToDomain(result.products, branchId),
        total: result.total,
        hasMore: result.hasMore,
      };
    },
    [engine],
  );

  const submitSale = useCallback(
    async (
      accessToken: string,
      payload: CreateSalePayload,
      options?: { cashierName?: string },
    ) => {
      ensureCatalogOrThrow();
      sessionRef.current = { accessToken, branchId: payload.branchId };
      const result = await engine.submitSaleLocalFirst(
        toOfflineSalePayload(payload),
        accessToken,
        undefined,
        { cashierName: options?.cashierName },
      );
      refreshFromEngine();
      if (result.mode === 'online') {
        void engine
          .pullDelta(accessToken, payload.branchId)
          .then(() => refreshFromEngine());
      }
      return {
        mode: result.mode,
        receiptId: result.receiptId,
        pending: result.pending,
      };
    },
    [engine, ensureCatalogOrThrow, refreshFromEngine],
  );

  const submitRefund = useCallback(
    async (accessToken: string, payload: OfflineRefundPayload) => {
      ensureCatalogOrThrow();
      sessionRef.current = { accessToken, branchId: payload.branchId };
      const result = await engine.submitRefundLocalFirst(payload, accessToken);
      refreshFromEngine();
      if (result.mode === 'online') {
        void engine
          .pullDelta(accessToken, payload.branchId)
          .then(() => refreshFromEngine());
      }
      return { mode: result.mode, pending: result.pending };
    },
    [engine, ensureCatalogOrThrow, refreshFromEngine],
  );

  const getLocalReceiptHistory = useCallback(
    (branchId: string) => engine.getSalesHistory(branchId),
    [engine],
  );

  const pullReceiptHistory = useCallback(
    async (accessToken: string, branchId: string) => {
      sessionRef.current = { accessToken, branchId };
      const count = await engine.pullSalesHistory(accessToken, branchId);
      refreshFromEngine();
      return count;
    },
    [engine, refreshFromEngine],
  );

  const value = useMemo<OfflinePosContextValue>(
    () => ({
      usesLocalCatalog: true,
      isOnline,
      connectivityReady,
      catalogReady,
      catalogRevision,
      isSyncing,
      lastSyncAt,
      pendingCount,
      firstLaunchBlocked,
      firstLaunchMessage: ONLINE_ONLY_MESSAGES.first_launch,
      canLoginOffline: loginOfflineAllowed,
      offlineLoginUnavailableMessage: ONLINE_ONLY_MESSAGES.offline_login_unavailable,
      onlineOnlyMessage: ONLINE_ONLY_MESSAGES,
      refreshConnectivity,
      syncNow,
      ensureCatalogOrThrow,
      getCachedBranches,
      cacheBranches,
      cacheAuthProfile,
      verifyOfflineLogin,
      unsealProfileRefreshToken,
      removeAuthProfile,
      searchLocalProducts,
      getLocalCategories,
      getLocalProductsByCategory,
      getLocalBranchProducts,
      submitSale,
      submitRefund,
      getLocalReceiptHistory,
      pullReceiptHistory,
      requireOnline,
    }),
    [
      isOnline,
      connectivityReady,
      catalogReady,
      catalogRevision,
      isSyncing,
      lastSyncAt,
      pendingCount,
      firstLaunchBlocked,
      loginOfflineAllowed,
      getCachedBranches,
      cacheBranches,
      cacheAuthProfile,
      verifyOfflineLogin,
      unsealProfileRefreshToken,
      removeAuthProfile,
      searchLocalProducts,
      getLocalCategories,
      getLocalProductsByCategory,
      getLocalBranchProducts,
      getLocalReceiptHistory,
      pullReceiptHistory,
      submitSale,
      submitRefund,
      requireOnline,
    ],
  );

  return (
    <OfflinePosContext.Provider value={value}>{children}</OfflinePosContext.Provider>
  );
}

export function OfflinePosProvider({ children }: { children: ReactNode }) {
  return supportsOfflinePos() ? (
    <ElectronOfflinePosProvider>{children}</ElectronOfflinePosProvider>
  ) : (
    <WebOnlinePosProvider>{children}</WebOnlinePosProvider>
  );
}

export function useOfflinePos(): OfflinePosContextValue {
  const ctx = useContext(OfflinePosContext);
  if (!ctx) {
    throw new Error('useOfflinePos must be used within OfflinePosProvider');
  }
  return ctx;
}
